/**
 * Page /mes-signalements — suivi des signalements de l'administré connecté (R13).
 *
 * Fix R13 du bug : "L'administré n'a accès qu'à la déclaration de fiches incidents.
 * Il n'a aucun onglet où il peut suivre quoi que ce soit."
 *
 * Cette page permet à un administré connecté de voir :
 *   - La liste de SES signalements (ceux qu'il a créés via le portail public OU
 *     via /service-technique/signaler quand il est connecté)
 *   - Le statut actuel de chaque (Reçu / Planifié / En cours / Résolu / Archivé)
 *   - Un lien vers la page de suivi détaillé /suivi/:code
 *
 * Backend : utilise la même route que public /api/public/signalements ou bien
 * /api/me/signalements selon ce qui est exposé. La route doit filtrer par
 * `createur_user_id = req.auth.userId` quand l'utilisateur est connecté.
 *
 * Si la route backend n'existe pas encore, créer côté serveur :
 *   GET /api/me/signalements
 *   → SELECT * FROM signalements WHERE createur_user_id = req.auth.userId
 *     ORDER BY created_at DESC LIMIT 50
 *   (filtré par RLS tenant côté DB)
 */

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  Card, Button, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AppHeader } from '@/components/AppHeader';
import {
  Landmark, ArrowLeft, MapPin, Plus, AlertCircle, MessageSquare,
  Construction, TreePine, Lightbulb, Trash2, Droplets, MoreHorizontal,
  ChevronRight,
} from 'lucide-react';
import { libelleSemainePlanifiee } from '@/lib/semaine-planifiee';

interface MonSignalement {
  id: string;
  codeSuivi: string | null;
  numero: string;
  sujet: string;
  categorie: string;
  urgence: string;
  statut: string;
  statutLabel: string;
  localisationAdresse: string | null;
  createdAt: string;
  resoluAt: string | null;
  /** M10 — lundi de la semaine d'intervention (jamais le jour exact). */
  semainePlanifiee?: string | null;
}

const CATEGORIE_ICONS: Record<string, typeof Construction> = {
  voirie: Construction,
  espaces_verts: TreePine,
  eclairage: Lightbulb,
  proprete: Trash2,
  assainissement: Droplets,
  autre: MoreHorizontal,
};

const URGENCE_VARIANT: Record<string, 'urgent' | 'warning' | 'success'> = {
  urgent: 'urgent',
  rapide: 'warning',
  normal: 'success',
};

const STATUT_VARIANT: Record<string, 'warning' | 'tech' | 'success' | 'neutral'> = {
  recu: 'warning',
  planifie: 'tech',
  en_cours: 'tech',
  resolu: 'success',
  archive: 'neutral',
};

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

export function MesSignalementsPage() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate(); // L13.10 — bug 1 fix : navigation au clic sur une card
  const [items, setItems] = useState<MonSignalement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Compteurs (item 8.2) ──────────────────────────────────────────────────
  // « En cours » = tout ce qui n'est ni résolu ni archivé : c'est ce que la
  // personne attend de suivre. Un signalement archivé n'est plus « en cours »,
  // mais il n'est pas non plus « résolu cette année » s'il date de l'an dernier.
  //
  // ⚠️ Europe/Paris, jamais toISOString() (règle projet) : un signalement résolu
  // le 1ᵉʳ janvier à 00h30 heure de Paris serait compté sur l'année précédente
  // en UTC.
  const anneeCourante = useMemo(
    () => Number(new Date().toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric' })),
    [],
  );
  const nbEnCours = useMemo(
    () => items.filter((s) => s.statut !== 'resolu' && s.statut !== 'archive').length,
    [items],
  );
  const nbResolusAnnee = useMemo(
    () =>
      items.filter((s) => {
        if (!s.resoluAt) return false;
        const annee = Number(
          new Date(s.resoluAt).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric' }),
        );
        return annee === anneeCourante;
      }).length,
    [items, anneeCourante],
  );

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.me.signalements.list();
        setItems(res.items ?? []);
      } catch (err) {
        if (err instanceof ApiException) setError(err.payload.error || 'Impossible de charger vos signalements');
        else setError('Erreur réseau');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Mes signalements techniques" accent="tech" />

      <main id="main-content" className="mx-auto max-w-3xl px-4 py-6 sm:px-6 animate-fade-in">
        {/* En-tête + bouton nouveau */}
        <header className="mb-6 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <LogoMark icon={MessageSquare} accent="tech" size="lg" />
            <div className="min-w-0">
              <SectionEyebrow as="div">Service public</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
                Mes signalements techniques
              </h1>
              <p className="mt-0.5 text-sm text-ink-muted">
                Bonjour {user?.prenom}. Retrouvez ici tous les signalements que vous avez créés.
              </p>
            </div>
          </div>
          <Link to="/service-technique/signaler">
            <Button variant="primary" size="md">
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Nouveau signalement</span>
            </Button>
          </Link>
        </header>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-4">
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div
                className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
                role="status"
                aria-live="polite"
              />
              <p className="mt-3 text-sm text-ink-muted">Chargement…</p>
            </div>
          </Card>
        )}

        {/* Vide */}
        {!loading && items.length === 0 && !error && (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <LogoMark icon={MessageSquare} accent="tech" size="lg" className="mx-auto" />
              <h2 className="mt-4 font-display text-lg font-semibold tracking-r-snug text-ink">
                Aucun signalement
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">
                Vous n'avez pas encore créé de signalement. Repérez un problème dans votre commune
                (voirie, éclairage, propreté) et signalez-le en 30 secondes.
              </p>
              <Link to="/service-technique/signaler" className="mt-6 inline-block">
                <Button variant="primary" size="lg">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Créer mon premier signalement
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Compteurs — item 8.2 du retour mairie :
            « compteur sur la vue administrée : signalements en cours +
              effectués/résolus depuis le début de l'année ».

            Calculé côté client sur `items` : la page charge déjà TOUS les
            signalements de la personne (statut + resoluAt sont dans le type).
            Aucune requête supplémentaire — ajouter un endpoint pour deux
            nombres qu'on a déjà serait un aller-retour pour rien.

            ⚠️ Année civile en Europe/Paris, jamais toISOString() (règle projet :
            un signalement résolu le 1er janvier à 00h30 tomberait sur l'année
            précédente en UTC). */}
        {!loading && items.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <Card variant="glass" padded>
              <div className="text-2xs uppercase tracking-r-wide text-ink-muted">En cours</div>
              <div className="mt-1 font-display text-2xl font-semibold text-ink">{nbEnCours}</div>
              <div className="mt-0.5 text-2xs text-ink-muted">
                {nbEnCours > 1 ? 'signalements suivis' : 'signalement suivi'}
              </div>
            </Card>
            <Card variant="glass" padded>
              <div className="text-2xs uppercase tracking-r-wide text-ink-muted">
                Résolus en {anneeCourante}
              </div>
              <div className="mt-1 font-display text-2xl font-semibold text-ink">{nbResolusAnnee}</div>
              <div className="mt-0.5 text-2xs text-ink-muted">
                {nbResolusAnnee > 1 ? 'depuis le 1ᵉʳ janvier' : 'depuis le 1ᵉʳ janvier'}
              </div>
            </Card>
          </div>
        )}

        {/* Liste */}
        {!loading && items.length > 0 && (
          <div className="space-y-3">
            {items.map((s) => {
              const CatIcon = CATEGORIE_ICONS[s.categorie] ?? MoreHorizontal;
              const dateCreated = new Date(s.createdAt).toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'short', year: 'numeric',
              });
              // L13.10 — bug 1 fix : la card est cliquable et redirige vers le détail
              // (codeSuivi si présent pour les anciens signalements, sinon route détail
              // service-technique standard avec garde administré côté backend).
              const detailPath = s.codeSuivi
                ? `/suivi/${s.codeSuivi}`
                : `/service-technique/signalements/${s.id}`;
              return (
                <Card
                  key={s.id}
                  variant="glass"
                  padded
                  onClick={() => navigate(detailPath)}
                  className="cursor-pointer transition-all hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  tabIndex={0}
                  role="link"
                  aria-label={`Ouvrir le signalement ${s.numero} — ${s.sujet}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(detailPath);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <LogoMark icon={CatIcon} accent="tech" size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={STATUT_VARIANT[s.statut] ?? 'neutral'} size="sm">
                          {s.statutLabel}
                        </Badge>
                        {libelleSemainePlanifiee(s.semainePlanifiee) && (
                          <span className="text-xs font-medium text-tech">
                            {libelleSemainePlanifiee(s.semainePlanifiee)}
                          </span>
                        )}
                        <Badge variant={URGENCE_VARIANT[s.urgence] ?? 'success'} size="sm">
                          Urgence {s.urgence}
                        </Badge>
                        <span className="text-xs text-ink-subtle">
                          <span className="font-mono">{s.numero}</span> · déposé le {dateCreated}
                        </span>
                      </div>
                      <h3 className="mt-2 font-display text-base font-semibold tracking-r-snug text-ink">
                        {s.sujet}
                      </h3>
                      {s.localisationAdresse && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
                          <MapPin className="h-3.5 w-3.5 text-tech-fg" aria-hidden="true" />
                          {s.localisationAdresse}
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-tech-fg">
                        Voir le détail
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
